import { Component, computed, OnDestroy, signal } from '@angular/core';
import {AgentDebugMetrics, AgentService, AgentStreamEvent} from "../../../_services/agent.service";
import { marked } from 'marked';
import {OrganizzazioniService} from "../../../_services/organizzazioni.service";
import {AuthenticationService} from "../../../_services/authentication.service";
import {constants} from "../../../_helpers/constants";

interface AgentDebugViewModel extends AgentDebugMetrics {
  totalElapsedMs: number | null;
  firstChunkMs: number | null;
  chunkCount: number;
  charCount: number;
  charsPerSecond: number | null;
}

@Component({
  selector: 'app-agent-report',
  templateUrl: './agent-report.component.html',
  styleUrls: ['./agent-report.component.scss'],
})
export class AgentReportComponent implements OnDestroy {

  constructor(
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private agentService: AgentService) { }

  prompt = '';
  reportContent = signal('');
  isLoading = signal(false);
  error = signal('');
  generatedAt = signal<Date | null>(null);
  debugInfo = signal<AgentDebugViewModel>(this.createEmptyDebugInfo());
  private requestStartedAt = 0;
  private firstChunkAt: number | null = null;
  private chunkCount = 0;
  private charCount = 0;
  private debugRefreshTimerId: number | null = null;

  get showDebugPanel(): boolean {
    return this.authService.isInRole(constants.ROLE_ADMIN);
  }

  generateStream(): void {
    if (!this.prompt.trim()) return;

    this.stopDebugTimer();
    this.reportContent.set('');
    this.error.set('');
    this.isLoading.set(true);
    this.generatedAt.set(new Date());
    this.requestStartedAt = performance.now();
    this.firstChunkAt = null;
    this.chunkCount = 0;
    this.charCount = 0;
    this.debugInfo.set(this.buildDebugInfo());
    this.startDebugTimer();

    this.agentService.streamReport(this.prompt, this.organizzazioniService.organizzazioneId).subscribe({
      next: (event) => this.handleStreamEvent(event),
      error: (err) => {
        const elapsedMs = this.getElapsedMs();
        this.error.set(err instanceof Error && err.message
          ? err.message
          : 'Errore durante la generazione. Riprova.');
        this.isLoading.set(false);
        this.stopDebugTimer();
        this.debugInfo.update(info => ({
          ...info,
          totalElapsedMs: elapsedMs,
          charsPerSecond: this.calculateCharsPerSecond(elapsedMs)
        }));
        console.error(err);
      },
      complete: () => {
        const elapsedMs = this.getElapsedMs();
        this.isLoading.set(false);
        this.stopDebugTimer();
        this.debugInfo.update(info => ({
          ...info,
          totalElapsedMs: elapsedMs,
          charsPerSecond: this.calculateCharsPerSecond(elapsedMs)
        }));
      }
    });
  }

  exportMarkdown(): void {
    const blob = new Blob([this.reportContent()], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${new Date().toISOString().slice(0,10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  renderedHtml = computed(() => marked(this.reportContent()));

  async copyToClipboard(): Promise<void> {
    await navigator.clipboard.writeText(this.reportContent());
  }

  formatDuration(ms?: number | null): string {
    if (ms == null) return '-';
    return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`;
  }

  formatNumber(value?: number | null, fractionDigits = 0): string {
    if (value == null) return '-';
    return value.toLocaleString('it-IT', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    });
  }

  ngOnDestroy(): void {
    this.stopDebugTimer();
  }

  private handleStreamEvent(event: AgentStreamEvent): void {
    if (event.type === 'meta') {
      this.debugInfo.update(info => ({
        ...info,
        ...event.metrics,
        totalElapsedMs: this.getElapsedMs(),
        charsPerSecond: this.calculateCharsPerSecond(this.getElapsedMs())
      }));
      return;
    }

    if (this.firstChunkAt === null) {
      this.firstChunkAt = performance.now();
    }

    this.chunkCount += 1;
    this.charCount += event.content.length;
    this.reportContent.update(v => v + event.content);
    this.debugInfo.set(this.buildDebugInfo(this.debugInfo()));
  }

  private buildDebugInfo(existing?: Partial<AgentDebugViewModel>): AgentDebugViewModel {
    const elapsedMs = this.getElapsedMs();

    return {
      model: existing?.model,
      serverElapsedMs: existing?.serverElapsedMs,
      dataCollectionMs: existing?.dataCollectionMs,
      ollamaTotalMs: existing?.ollamaTotalMs,
      loadMs: existing?.loadMs,
      promptTokens: existing?.promptTokens,
      outputTokens: existing?.outputTokens,
      outputTokensPerSecond: existing?.outputTokensPerSecond,
      outputTokensPerMinute: existing?.outputTokensPerMinute,
      doneReason: existing?.doneReason,
      totalElapsedMs: elapsedMs,
      firstChunkMs: this.firstChunkAt === null ? null : Math.round(this.firstChunkAt - this.requestStartedAt),
      chunkCount: this.chunkCount,
      charCount: this.charCount,
      charsPerSecond: this.calculateCharsPerSecond(elapsedMs)
    };
  }

  private getElapsedMs(): number | null {
    return this.requestStartedAt > 0
      ? Math.round(performance.now() - this.requestStartedAt)
      : null;
  }

  private calculateCharsPerSecond(elapsedMs: number | null): number | null {
    if (!elapsedMs || elapsedMs <= 0 || this.charCount === 0) return null;
    return this.charCount / (elapsedMs / 1000);
  }

  private startDebugTimer(): void {
    this.debugRefreshTimerId = window.setInterval(() => {
      if (!this.isLoading()) {
        return;
      }

      const elapsedMs = this.getElapsedMs();
      this.debugInfo.update(info => ({
        ...info,
        totalElapsedMs: elapsedMs,
        charsPerSecond: this.calculateCharsPerSecond(elapsedMs)
      }));
    }, 250);
  }

  private stopDebugTimer(): void {
    if (this.debugRefreshTimerId === null) {
      return;
    }

    window.clearInterval(this.debugRefreshTimerId);
    this.debugRefreshTimerId = null;
  }

  private createEmptyDebugInfo(): AgentDebugViewModel {
    return {
      totalElapsedMs: null,
      firstChunkMs: null,
      chunkCount: 0,
      charCount: 0,
      charsPerSecond: null
    };
  }

}
